import React, { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Collapse,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
  TextInput,
  Textarea,
  UnstyledButton,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconChevronDown, IconPlus, IconTarget } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useCreateGoal, useUserGoals } from '../../../hooks/useApi';
import { Goal } from '../../../types';
import { showToast } from '../../../utils/toast';
import { ApiError, getErrorMessage } from '../../../utils/api';
import { getGoalStatusColor } from '../../../utils/statusUtils';
import { formatDay, goalDeadlineDay } from '../../../utils/date';

interface SubjectGoalsPanelProps {
  userId: string;
  // The 1-on-1 is run off this panel, so the lead/PM/admin reading the summary can
  // write the agreements down while they are talking. Everyone else (the subject
  // reading their own result) sees the same list without the add button.
  canAssign?: boolean;
  // In the self-review form the panel is a reminder of what was agreed last time —
  // with nothing agreed, it is just noise, so it disappears entirely.
  hideWhenEmpty?: boolean;
  heading?: string;
}

export const SubjectGoalsPanel: React.FC<SubjectGoalsPanelProps> = ({
  userId,
  canAssign,
  hideWhenEmpty,
  heading,
}) => {
  const { t, i18n } = useTranslation();
  const { data: goals = [], error: goalsError, isLoading } = useUserGoals(userId);
  const createGoal = useCreateGoal();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDoneOpen, setIsDoneOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // `YYYY-MM-DD` от Mantine 8: срок — день в поясе компании, его считает сервер.
  const [deadline, setDeadline] = useState<string | null>(null);

  // Цели видят не все, кому виден итог (например, наблюдатель): без доступа
  // «договорённостей пока нет» было бы неправдой, поэтому панели нет вовсе.
  if (goalsError instanceof ApiError && goalsError.status === 403) return null;

  const open = goals.filter((g) => g.status !== 'completed');
  const done = goals.filter((g) => g.status === 'completed');

  const handleCreate = async () => {
    if (!title.trim() || !description.trim()) return;
    try {
      await createGoal.mutateAsync({
        title: title.trim(),
        description: description.trim(),
        assigneeId: userId,
        deadline: deadline ?? undefined,
      });
      setTitle('');
      setDescription('');
      setDeadline(null);
      setIsFormOpen(false);
      showToast({ title: t('perfReview.goals.created'), message: '', color: 'teal' });
    } catch (e) {
      showToast({
        title: t('perfReview.goals.createFailed'),
        message: getErrorMessage(e, ''),
        color: 'red',
      });
    }
  };

  // One line per agreement: dot, title, due date. The description and the closing
  // comment are one click away — a lead scanning four reports before a 1-on-1 wants
  // the list, not the essays.
  const renderGoal = (g: Goal) => {
    const isExpanded = expanded === g._id;
    const hasDetail = !!g.description?.trim() || !!g.completionComment?.trim();
    return (
      <Box key={g._id}>
        <UnstyledButton
          onClick={() => hasDetail && setExpanded(isExpanded ? null : g._id)}
          style={{ width: '100%', cursor: hasDetail ? 'pointer' : 'default' }}
        >
          <Group gap="xs" wrap="nowrap" py={4}>
            <Box
              style={{
                width: 7,
                height: 7,
                borderRadius: 999,
                flexShrink: 0,
                background: `var(--mantine-color-${getGoalStatusColor(g)}-6)`,
              }}
            />
            <Text
              size="sm"
              fw={500}
              td={g.status === 'completed' ? 'line-through' : undefined}
              c={g.status === 'completed' ? 'dimmed' : undefined}
              style={{ flex: 1, minWidth: 0 }}
              truncate
            >
              {g.title}
            </Text>
            {g.deadline && (
              <Text size="xs" c={g.isOverdue ? 'red' : 'dimmed'} style={{ flexShrink: 0 }}>
                {formatDay(goalDeadlineDay(g), i18n.language)}
              </Text>
            )}
            {hasDetail && (
              <IconChevronDown
                size={13}
                style={{
                  flexShrink: 0,
                  color: 'var(--mantine-color-dimmed)',
                  transform: isExpanded ? 'rotate(180deg)' : undefined,
                  transition: 'transform 150ms',
                }}
              />
            )}
          </Group>
        </UnstyledButton>
        <Collapse in={isExpanded}>
          <Box pl={15} pb={6}>
            {g.description && (
              <Text size="sm" c="dimmed" lh={1.5} style={{ whiteSpace: 'pre-wrap' }}>
                {g.description}
              </Text>
            )}
            {g.completionComment && (
              <Text size="sm" mt={4} lh={1.5} style={{ whiteSpace: 'pre-wrap' }}>
                {t('perfReview.goals.result')}: {g.completionComment}
              </Text>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  // Пока цели грузятся или не загрузились, «договорённостей нет» было бы неправдой.
  if (hideWhenEmpty && (isLoading || goalsError || !goals.length)) return null;

  return (
    <>
      <Paper withBorder p="md" radius="md" mb="md">
        <Group justify="space-between" mb={open.length || done.length ? 'sm' : 0}>
          <Group gap="xs">
            <IconTarget size={16} />
            <Text fw={600} size="sm">
              {heading ?? t('perfReview.goals.title')}
            </Text>
            {open.length > 0 && (
              <Badge size="sm" variant="light" color="blue">
                {open.length}
              </Badge>
            )}
          </Group>
          {canAssign && (
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPlus size={14} />}
              onClick={() => setIsFormOpen(true)}
            >
              {t('perfReview.goals.add')}
            </Button>
          )}
        </Group>

        {goalsError ? (
          <Text size="sm" c="red">
            {t('perfReview.goals.loadFailed')}
          </Text>
        ) : isLoading ? (
          <Loader size="xs" />
        ) : !goals.length ? (
          <Text size="sm" c="dimmed">
            {t('perfReview.goals.empty')}
          </Text>
        ) : (
          <Stack gap={2}>
            {open.length ? (
              open.map(renderGoal)
            ) : (
              <Text size="sm" c="dimmed">
                {t('perfReview.goals.allDone')}
              </Text>
            )}

            {/* Closed agreements are history: they matter when writing the next
                summary, but they should not push the live ones off the screen. */}
            {done.length > 0 && (
              <>
                <UnstyledButton onClick={() => setIsDoneOpen((v) => !v)} mt={6}>
                  <Group gap={4}>
                    <Text size="xs" c="dimmed">
                      {t('perfReview.goals.showDone', { count: done.length })}
                    </Text>
                    <IconChevronDown
                      size={12}
                      style={{
                        color: 'var(--mantine-color-dimmed)',
                        transform: isDoneOpen ? 'rotate(180deg)' : undefined,
                        transition: 'transform 150ms',
                      }}
                    />
                  </Group>
                </UnstyledButton>
                <Collapse in={isDoneOpen}>
                  <Stack gap={2} mt={2}>
                    {done.map(renderGoal)}
                  </Stack>
                </Collapse>
              </>
            )}
          </Stack>
        )}
      </Paper>

      <Modal
        opened={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        title={t('perfReview.goals.add')}
        centered
      >
        <Stack gap="sm">
          <TextInput
            label={t('perfReview.goals.fieldTitle')}
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
            required
          />
          <Textarea
            label={t('perfReview.goals.fieldDescription')}
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            autosize
            minRows={3}
            required
          />
          <DateInput
            label={t('perfReview.goals.fieldDeadline')}
            value={deadline}
            onChange={setDeadline}
            minDate={new Date()}
            clearable
          />
          <Group justify="flex-end" gap="xs" mt="xs">
            <Button variant="subtle" color="gray" onClick={() => setIsFormOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              loading={createGoal.isPending}
              onClick={handleCreate}
              disabled={!title.trim() || !description.trim()}
            >
              {t('perfReview.goals.save')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};
